import { DestroyRef, Injectable, Renderer2, RendererFactory2, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Subject } from 'rxjs'
import { ModalService } from './modal.service'

@Injectable()
export class MobileFilterDrawerService {
  public readonly isOpen = signal(false)
  public readonly closed$ = new Subject<void>()

  private readonly _destroyRef = inject(DestroyRef)
  private readonly _modalService = inject(ModalService)
  private readonly _renderer: Renderer2 = inject(RendererFactory2).createRenderer(null, null)

  private _didLockScroll = false
  private _host: HTMLElement | null = null
  private _portalOriginalParent: HTMLElement | null = null

  constructor() {
    this.closed$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this._unlockBodyScroll()
    })

    this._destroyRef.onDestroy(() => {
      this._unlockBodyScroll()
      this.unregisterHost()
    })
  }

  /**
   * Moves the drawer host to `document.body` so it renders above the app navbar.
   */
  public registerHost(host: HTMLElement): void {
    if (this._host === host && host.parentElement === document.body) return

    this._host = host
    this._portalOriginalParent = host.parentElement

    if (host.parentElement !== document.body) {
      this._renderer.appendChild(document.body, host)
    }
  }

  /**
   * Returns the drawer host to its original parent on teardown.
   */
  public unregisterHost(): void {
    if (!this._host) return

    if (this._host.parentElement === document.body && this._portalOriginalParent) {
      this._renderer.appendChild(this._portalOriginalParent, this._host)
    }

    this._host = null
    this._portalOriginalParent = null
  }

  public open(): void {
    if (this.isOpen()) return

    this.isOpen.set(true)
    this._lockBodyScroll()
  }

  public close(): void {
    if (!this.isOpen()) return

    this.isOpen.set(false)
    this.closed$.next()
  }

  public toggle(): void {
    if (this.isOpen()) {
      this.close()
      return
    }

    this.open()
  }

  /**
   * Locks the body scroll.
   */
  private _lockBodyScroll(): void {
    if (this._didLockScroll) return

    this._renderer.addClass(document.body, 'tw-overflow-hidden')
    this._didLockScroll = true
  }

  /**
   * Unlocks the body scroll.
   */
  private _unlockBodyScroll(): void {
    if (!this._didLockScroll) return

    this._didLockScroll = false
    if (!this._modalService.hasOpenModal()) {
      this._renderer.removeClass(document.body, 'tw-overflow-hidden')
    }
  }
}
